import { Currency } from "@utils/Currency";
import { OMath } from "@utils/OMath";
import dayjs from "dayjs";
import { Environment } from "./Environment";
import { DeliveryType } from "@olga-food/schemas/lib/classes/schemas/order/delivery/Delivery";
import { OrderStorage } from "storage/OrderStorage";
import { CatalogHelpers } from "shared/tools/CatalogHelpers";
import { Builder } from "@olga-food/schemas/lib/classes/schemas/catalog/Builder";

export const GIFT_TYPE = {
  ADDITION: 1,
  PRICE: 2,
  PERCENT: 4,
  CART: 8,
  DELIVERY: 16,
  FIRST_PURCHASE_EVER: 32,
  N_USE: 64,
  FREE_TO_USE: 128,
  NEXT_PURCHARSE: 256,
  CASHBACK_USE: 512,
  CASHBACK_EARN: 1024,
  PRODUCT: 2048,
};

const USE_TYPES = {
  ONLINE: 1,
  OFFLINE: 2,
  TAKEAWAY: 4,
  FASTFILA: 8,
};

const isType = (haystack, needle) => (needle & haystack) >= needle;

export class Benefit {
  static getTitle(coupon, programByPoint) {
    switch (coupon?.type) {
      case "PERCENTAGE_DISCOUNT":
      case "PRODUCT_DISCOUNT":
        return `Cupom de ${coupon.value || 0}%`;
      case "PRODUCT_CASH_DISCOUNT":
        return `Cupom de ${Currency.formatCurrency(coupon.amount || 0)}`;
      case "CASH_DISCOUNT":
        return `Cupom de ${Currency.formatCurrency(coupon.value || 0)}`;
      case "DISCOUNT_ORDER":
        if (coupon.amount_type === "CASH")
          return `Cupom de ${Currency.formatCurrency(coupon.amount || 0)}`;

        if (coupon.amount_type === "PERCENT")
          return `Cupom de ${coupon.amount || 0}%`;
        break;
      case "CASHBACK_ACCUMULATE":
        if (coupon.cashback_cash_bonus)
          return `Cashback de ${Currency.formatCurrency(
            coupon.cashback_cash_bonus || 0,
          )}`;
        if (coupon.cashback_percent_bonus)
          return `Cashback de ${coupon.cashback_percent_bonus || 0}%`;
        break;
      case "CASHBACK":
        return programByPoint
          ? `${coupon.amount || coupon.value} pontos para cada R$100`
          : `Cashback de ${coupon.amount || coupon.value || 0}%`;
      case "FREE_DELIVERY":
        return `Cupom de Frete grátis`;
      case "CASHBACK_CONSUME":
        return `Usar saldo`;
      default:
        break;
    }

    return "Cupom não encontrado";
  }

  static getExpirationStatus(expirationDate, expirationDays) {
    if (!expirationDate && !expirationDays) return;

    if (expirationDays) return `Expira em ${expirationDays} dia(s)`;

    return `Expira em ${dayjs(expirationDate).format("DD/MM/YYYY")}`;
  }

  static getDiscountText(coupon, order_total = 0) {
    switch (coupon.type) {
      case "CASH_DISCOUNT":
      case "PRODUCT_CASH_DISCOUNT":
        return Currency.formatCurrency(coupon.value || 0);
      case "PERCENTAGE_DISCOUNT":
        return Currency.formatCurrency(
          order_total * ((coupon.value || 0) / 100),
        );
      case "PRODUCT_DISCOUNT":
      case "CASHBACK":
        return Currency.formatCurrency(
          OMath.times(coupon.value || 0, order_total || 0, 0.01),
        );
      case "FREE_DELIVERY":
        return "Frete Grátis";
      default:
        return "Desconhecido";
    }
  }

  static getRules(coupon) {
    const rules = [];

    const expirationDays = coupon?.expiration_days;
    const expirationDate = coupon?.expiration_date;
    if (!expirationDate && !expirationDays) rules.push("Nunca expira");
    else {
      const expirationStatus = this.getExpirationStatus(
        expirationDate || expirationDays,
      );
      rules.push(expirationStatus);
    }

    if (coupon.type == "FREE_DELIVERY")
      rules.push("Aplicado somente ao valor da entrega");

    if (!!coupon.min_order)
      rules.push(
        `Pedido mínimo ${Currency.formatCurrency(coupon.min_order || 0)}`,
      );

    if (["PRODUCT_CASH_DISCOUNT", "PRODUCT_DISCOUNT"].includes(coupon.type))
      rules.push("Aplicado somente ao valor de produtos selecionados");

    if (isType(coupon.type_use, USE_TYPES.FASTFILA))
      rules.push(`Disponível para retirada pedindo no app (comer aqui)`);

    if (isType(coupon.type_use, USE_TYPES.TAKEAWAY))
      rules.push(`Disponível para retirada pedindo no app (levar)`);

    if (isType(coupon.type_use, USE_TYPES.ONLINE))
      rules.push(`Disponível para entrega`);

    if (isType(coupon.type_use, USE_TYPES.OFFLINE))
      rules.push(`Disponível para salão`);

    return rules;
  }

  static getCode(coupon) {
    if (!coupon?.code) return null;
    if (Environment.id === 51) return `${coupon?.code}`;
    if (coupon?.code) return `${coupon?.code}-${coupon?.pin}`;
    else return null;
  }

  static canBeUsed(coupon, isOffline = false) {
    if (coupon?.type) {
      const { cart, delivery } = OrderStorage.getOrder();

      if (coupon.enabled === false) return false;

      if (
        coupon.visibility?.length > 0 &&
        !Benefit.isCurrentTimeValid(coupon.visibility)
      )
        return false;

      if (isOffline) return true;

      if (coupon.type == "CASHBACK_CONSUME") return true;

      if (
        coupon.type === "FREE_DELIVERY" &&
        delivery?.type !== DeliveryType.DELIVER_ON_ADDRESS
      )
        return;

      if (cart.total < coupon.min_order) return false;

      if (["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type)) {
        return Benefit.isValidProductDiscount(coupon, cart);
      }

      const isFastFilaOk =
        (isType(coupon.type_use, USE_TYPES.FASTFILA) ||
          isType(coupon.type_use, USE_TYPES.OFFLINE)) &&
        delivery?.type == DeliveryType.INDOOR;
      const isTakeAwayOk =
        isType(coupon.type_use, USE_TYPES.TAKEAWAY) &&
        delivery?.type == DeliveryType.GET_ON_PLACE;
      const isDeliveryOk =
        isType(coupon.type_use, USE_TYPES.ONLINE) &&
        delivery?.type == DeliveryType.DELIVER_ON_ADDRESS;

      if (!(isFastFilaOk || isTakeAwayOk || isDeliveryOk)) return false;

      return true;
    }

    return false;
  }

  static isCurrentTimeValid(visibility) {
    const currentDayOfWeek = dayjs().day();

    const daySchedule = visibility.find(
      (item) => item.weekDay === currentDayOfWeek,
    );

    if (!daySchedule) return false;

    return daySchedule.times.some((timeRange) => {
      const start = Number(timeRange.start);
      const end = Number(timeRange.end);
      const currentTime = Number(dayjs().format("HHmm"));

      return currentTime >= start && currentTime <= end;
    });
  }

  static getPlacesCanBeUsed(coupon) {
    if (!coupon.type_use) return null;

    let places = "Disponível para";

    const types = [];

    if (isType(coupon.type_use, USE_TYPES.TAKEAWAY)) types.push(`retirada`);

    if (isType(coupon.type_use, USE_TYPES.ONLINE)) types.push(`delivery`);

    if (isType(coupon.type_use, USE_TYPES.OFFLINE)) types.push(`loja`);

    if (types.length === 1) places = places.concat(` ${types[0]}`);
    else if (types.length > 1) {
      const lastType = types.pop();
      places = places.concat(` ${types.join(", ")} e ${lastType}`);
    }

    return places;
  }

  static parseCouponType(coupon) {
    switch (coupon.type) {
      case "CASH_DISCOUNT":
        return GIFT_TYPE.PRICE | GIFT_TYPE.CART | GIFT_TYPE.FREE_TO_USE;
      case "PERCENTAGE_DISCOUNT":
        return GIFT_TYPE.PERCENT | GIFT_TYPE.CART | GIFT_TYPE.FREE_TO_USE;
      case "PRODUCT_DISCOUNT":
        return GIFT_TYPE.PERCENT | GIFT_TYPE.PRODUCT | GIFT_TYPE.FREE_TO_USE;
      case "PRODUCT_CASH_DISCOUNT":
        return GIFT_TYPE.PRICE | GIFT_TYPE.PRODUCT | GIFT_TYPE.FREE_TO_USE;
      case "CASHBACK":
        return (
          GIFT_TYPE.CASHBACK_EARN |
          GIFT_TYPE.PERCENT |
          GIFT_TYPE.CART |
          GIFT_TYPE.FREE_TO_USE
        );
      case "CASHBACK_CONSUME":
        return (
          GIFT_TYPE.CASHBACK_USE |
          GIFT_TYPE.PRICE |
          GIFT_TYPE.CART |
          GIFT_TYPE.FREE_TO_USE
        );
      case "FREE_DELIVERY":
        return GIFT_TYPE.PRICE | GIFT_TYPE.DELIVERY | GIFT_TYPE.FREE_TO_USE;
      default:
        break;
    }

    return null;
  }

  static getDescription(coupon, order) {
    switch (coupon.type) {
      case "PRODUCT_DISCOUNT":
      case "PRODUCT_CASH_DISCOUNT":
        return coupon.description || Benefit.getTitle(coupon, order);
      default:
        Benefit.getTitle(coupon, order);
        break;
    }
  }

  static isValidProductDiscount(coupon, cart) {
    const couponProducts = coupon?.product_list || [];
    const allowMultipleItems = coupon?.multiple_items;
    const cartCatalog = CatalogHelpers.clearUnselectedItems(cart?.catalog) || {
      items: [],
    };

    const props = ["items", "sizes"];
    let validSkuCount = {};

    const generateUniqueKey = (item) => {
      const randomSuffix = Math.floor(Math.random() * 100000)
        .toString()
        .padStart(5, "0");
      return `${item.id}-${randomSuffix}`;
    };

    const validateAndCountSKUs = (item) => {
      let localValidSkuCount = 0;
      for (const key of props) {
        if (item[key] && item[key].length > 0) {
          for (const subItemIndex in item[key]) {
            item[key][subItemIndex] = validateAndCountSKUs(
              item[key][subItemIndex],
            );
          }

          const validItems = item[key].filter(
            (i) => i && couponProducts.includes(i.sku),
          );

          localValidSkuCount += validItems.length;
          item[key] = validItems.length > 0 ? validItems : null;
        }
      }

      if (localValidSkuCount > 0) {
        const uniqueKey = generateUniqueKey(item);
        validSkuCount[uniqueKey] =
          (validSkuCount[uniqueKey] || 0) + localValidSkuCount;
      }
      return item;
    };

    Builder.executeOnSchema(cartCatalog, (item) => validateAndCountSKUs(item));

    let isValid;

    for (const [key, count] of Object.entries(validSkuCount)) {
      if (allowMultipleItems) isValid = count >= 1;
      else isValid = count === 1;
    }

    return isValid;
  }

  static getCouponTitle(coupon, programByPoint) {
    switch (coupon?.type) {
      case "PERCENTAGE_DISCOUNT":
        return `Cupom ${coupon.value || 0}% de desconto`;
      case "CASH_DISCOUNT":
      case "PRODUCT_CASH_DISCOUNT":
        return `Cupom de ${Currency.formatCurrency(coupon.value || 0)} de desconto`;
      case "CASHBACK":
        return programByPoint
          ? `${coupon.amount || coupon.value} pontos para cada R$100`
          : `Cupom de ${coupon.amount || coupon.value || 0}% de cashback`;
      case "FREE_DELIVERY":
        return `Cupom de frete grátis`;
      default:
        break;
    }
  }

  static parseCouponToOrderGift(coupon, cartItems = []) {
    const gift = {
      id: coupon.coupon || coupon.id,
      code: Benefit.getCode(coupon),
      type: Benefit.parseCouponType(coupon),
      value: coupon.value || 0,
      ...(coupon.type == "CASHBACK_CONSUME" ? { amount: coupon.amount } : {}),
      min_order: coupon.min_order || coupon.min_order_amount,
      expiration: coupon.expiration?.end,
      ...(["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon.type)
        ? {
            target_id: CatalogHelpers.getFirstItemSkuOnSkuList(
              cartItems,
              coupon.product_list,
            ),
          }
        : {}),
      created_at: coupon.created_at,
    };

    const giftSchema = gift;

    return giftSchema;
  }
}
